import { LoadingButton } from '@mui/lab';
import {
    Alert,
    Autocomplete,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import useSWR from 'swr';
import server from '../../../global/server';
import useHandleErrors from '../../../hooks/useHandleErrors';

const SERVER = process.env.REACT_APP_SERVER;
const DEFAULT_WEBHOOK_URL = `${SERVER}/public/webhooks/asaas`;

const PAYMENT_EVENTS = [
    'PAYMENT_AUTHORIZED',
    'PAYMENT_AWAITING_RISK_ANALYSIS',
    'PAYMENT_APPROVED_BY_RISK_ANALYSIS',
    'PAYMENT_REPROVED_BY_RISK_ANALYSIS',
    'PAYMENT_CREATED',
    'PAYMENT_UPDATED',
    'PAYMENT_CONFIRMED',
    'PAYMENT_RECEIVED',
    'PAYMENT_ANTICIPATED',
    'PAYMENT_OVERDUE',
    'PAYMENT_DELETED',
    'PAYMENT_RESTORED',
    'PAYMENT_REFUNDED',
    'PAYMENT_REFUND_IN_PROGRESS',
    'PAYMENT_REFUND_DENIED',
    'PAYMENT_RECEIVED_IN_CASH_UNDONE',
    'PAYMENT_CHARGEBACK_REQUESTED',
    'PAYMENT_CHARGEBACK_DISPUTE',
    'PAYMENT_AWAITING_CHARGEBACK_REVERSAL',
    'PAYMENT_DUNNING_RECEIVED',
    'PAYMENT_DUNNING_REQUESTED',
    'PAYMENT_BANK_SLIP_CANCELLED',
    'PAYMENT_BANK_SLIP_VIEWED',
    'PAYMENT_CHECKOUT_VIEWED',
    'PAYMENT_CREDIT_CARD_CAPTURE_REFUSED',
    'PAYMENT_PARTIALLY_REFUNDED',
    'PAYMENT_SPLIT_CANCELLED',
    'PAYMENT_SPLIT_DIVERGENCE_BLOCK',
    'PAYMENT_SPLIT_DIVERGENCE_BLOCK_FINISHED',
];

const DEFAULT_EVENTS = [
    'PAYMENT_CREATED',
    'PAYMENT_UPDATED',
    'PAYMENT_CONFIRMED',
    'PAYMENT_RECEIVED',
    'PAYMENT_OVERDUE',
    'PAYMENT_REFUNDED',
];

const initialFormValues = {
    name: '',
    email: '',
    url: DEFAULT_WEBHOOK_URL,
    enabled: true,
    interrupted: false,
    sendType: 'SEQUENTIALLY',
    authToken: '',
    events: DEFAULT_EVENTS,
};

function buildPayload(formValues) {
    const { id, name, email, url, enabled, interrupted, sendType, authToken, events } = formValues;

    return {
        ...(id ? { id } : {}),
        name,
        email,
        url,
        enabled,
        interrupted,
        sendType,
        ...(authToken?.trim() ? { authToken: authToken.trim() } : {}),
        events,
    };
}

function getApiErrorMessage(error) {
    const data = error?.response?.data;
    const status = error?.response?.status;

    if (typeof data === 'string' && data.trim()) {
        return data.trim();
    }

    if (typeof data?.message === 'string' && data.message.trim()) {
        return data.message;
    }

    if (Array.isArray(data?.errors)) {
        const messages = data.errors
            .map(item => item?.description || item?.detail || item?.message || item?.code)
            .filter(Boolean);

        if (messages.length) {
            return messages.join(' • ');
        }
    }

    if (typeof error?.message === 'string' && error.message.trim()) {
        return status ? `[HTTP ${status}] ${error.message}` : error.message;
    }

    return status
        ? `Erro HTTP ${status}. Não foi possível salvar o webhook.`
        : 'Não foi possível salvar o webhook. Verifique os dados e tente novamente.';
}

function reportError(error, setSubmitError, setErrs, enqueueSnackbar) {
    const message = getApiErrorMessage(error);
    setSubmitError(message);
    setErrs({ ...error, _reportedAt: Date.now() });
    enqueueSnackbar(message, { variant: 'error', autoHideDuration: 8000 });
}

function validateForm(formValues) {
    const messages = [];

    if (!formValues.name?.trim()) messages.push('Informe o nome do webhook');
    if (!formValues.email?.trim()) messages.push('Informe o e-mail de notificação');
    if (!formValues.url?.trim()) messages.push('Informe a URL do webhook');
    if (!Array.isArray(formValues.events) || !formValues.events.length) {
        messages.push('Selecione ao menos um evento');
    }

    return messages;
}

export default function WebhooksList() {
    const { enqueueSnackbar } = useSnackbar();
    const [errs, setErrs] = useState();
    const [submitError, setSubmitError] = useState('');
    const [viewDialog, setViewDialog] = useState(false);
    const [submittingDelete, setSubmittingDelete] = useState(false);

    const {
        data: webhooksResponse,
        error: fetchError,
        mutate,
        isLoading,
    } = useSWR('/asaas/webhooks', async url => {
        const { data } = await server.get(url);
        return data;
    });

    const rows = webhooksResponse?.data || [];
    const asaasEnvironment = webhooksResponse?.meta?.asaasEnvironment;
    const listError = fetchError ? getApiErrorMessage(fetchError) : '';

    useHandleErrors(errs);

    const { handleChange, handleSubmit, handleReset, setValues, setFieldValue, values, isSubmitting } = useFormik({
        initialValues: initialFormValues,
        onSubmit: async (formValues, { setSubmitting }) => {
            const validationErrors = validateForm(formValues);
            if (validationErrors.length) {
                const message = validationErrors.join(' • ');
                setSubmitError(message);
                enqueueSnackbar(message, { variant: 'error', autoHideDuration: 8000 });
                return;
            }

            try {
                setSubmitting(true);
                setSubmitError('');
                const payload = buildPayload(formValues);

                if (formValues.id) {
                    const { data } = await server.put(`/asaas/webhooks/${formValues.id}`, payload);
                    if (!data?.data?.id) {
                        throw { message: 'Servidor não confirmou a atualização do webhook' };
                    }
                    enqueueSnackbar(`Webhook atualizado (${data.data.id})`);
                } else {
                    const { data } = await server.post('/asaas/webhooks', payload);
                    if (!data?.data?.id) {
                        throw { message: 'Servidor não confirmou a criação do webhook' };
                    }
                    enqueueSnackbar(`Webhook cadastrado (${data.data.id})`);
                }

                setSubmitting(false);
                await mutate();
                handleReset();
                setSubmitError('');
                setViewDialog(false);
            } catch (error) {
                setSubmitting(false);
                reportError(error, setSubmitError, setErrs, enqueueSnackbar);
            }
        },
    });

    const openCreateDialog = () => {
        handleReset();
        setSubmitError('');
        setValues({ ...initialFormValues }, false);
        setViewDialog(true);
    };

    const openEditDialog = row => {
        setSubmitError('');
        setValues(
            {
                id: row.id,
                name: row.name || '',
                email: row.email || '',
                url: row.url || '',
                enabled: row.enabled ?? true,
                interrupted: row.interrupted ?? false,
                sendType: row.sendType || 'SEQUENTIALLY',
                authToken: row.authToken || '',
                events: row.events || [],
            },
            false,
        );
        setViewDialog(true);
    };

    const closeDialog = () => {
        handleReset();
        setSubmitError('');
        setViewDialog(false);
    };

    const excluir = async id => {
        try {
            setSubmittingDelete(true);
            setSubmitError('');
            await server.delete(`/asaas/webhooks/${id}`);
            enqueueSnackbar('Webhook removido');
            setSubmittingDelete(false);
            mutate();
            closeDialog();
        } catch (error) {
            setSubmittingDelete(false);
            reportError(error, setSubmitError, setErrs, enqueueSnackbar);
        }
    };

    const columns = useMemo(
        () => [
            { field: 'name', headerName: 'Nome', flex: 1, minWidth: 140 },
            { field: 'url', headerName: 'URL', flex: 2, minWidth: 220 },
            {
                field: 'enabled',
                headerName: 'Ativo',
                width: 90,
                renderCell: params =>
                    params.value ? <Chip color="success" size="small" label="Sim" /> : <Chip color="default" size="small" label="Não" />,
            },
            {
                field: 'interrupted',
                headerName: 'Fila',
                width: 110,
                renderCell: params =>
                    params.value ? <Chip color="warning" size="small" label="Interrompida" /> : <Chip color="success" size="small" label="Normal" />,
            },
            {
                field: 'events',
                headerName: 'Eventos',
                width: 100,
                valueGetter: value => (Array.isArray(value) ? value.length : 0),
            },
            {
                field: 'actions',
                type: 'actions',
                width: 100,
                renderCell: params => (
                    <Button size="small" onClick={() => openEditDialog(params.row)}>
                        Editar
                    </Button>
                ),
            },
        ],
        [],
    );

    return (
        <>
            <Grid container direction="row" alignItems="center" spacing={2} sx={{ marginBottom: 2 }}>
                <Grid item xs={12} md={8}>
                    <Typography variant="body2" color="text.secondary">
                        Gerencie os webhooks da conta Asaas. A URL padrão do PokeMed é{' '}
                        <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {DEFAULT_WEBHOOK_URL}
                        </Typography>
                    </Typography>
                    {asaasEnvironment && (
                        <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                            Ambiente Asaas conectado:{' '}
                            <strong>{asaasEnvironment === 'sandbox' ? 'Sandbox' : asaasEnvironment === 'production' ? 'Produção' : 'Desconhecido'}</strong>
                            {asaasEnvironment === 'sandbox' && ' — confira os webhooks no painel sandbox.asaas.com, não no painel de produção.'}
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                    <Button variant="contained" onClick={openCreateDialog}>
                        Cadastrar webhook
                    </Button>
                </Grid>
            </Grid>

            {listError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Não foi possível carregar os webhooks: {listError}
                </Alert>
            )}

            <DataGrid
                autoHeight
                loading={isLoading}
                rows={rows}
                columns={columns}
                getRowId={row => row.id}
                disableRowSelectionOnClick
            />

            <Dialog open={viewDialog} onClose={closeDialog} maxWidth="md" fullWidth>
                <DialogTitle>{values.id ? 'Editar webhook Asaas' : 'Novo webhook Asaas'}</DialogTitle>
                <form
                    noValidate
                    onSubmit={event => {
                        event.preventDefault();
                        handleSubmit(event);
                    }}
                >
                <DialogContent>
                    {submitError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {submitError}
                        </Alert>
                    )}
                    <Grid container spacing={2} mt={0.5}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                required
                                name="name"
                                label="Nome"
                                value={values.name}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                required
                                type="email"
                                name="email"
                                label="E-mail de notificação"
                                value={values.email}
                                onChange={handleChange}
                                helperText="E-mail exigido pelo Asaas para alertas do webhook"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel id="sendType-label">Tipo de envio</InputLabel>
                                <Select
                                    labelId="sendType-label"
                                    name="sendType"
                                    label="Tipo de envio"
                                    value={values.sendType}
                                    onChange={handleChange}
                                >
                                    <MenuItem value="SEQUENTIALLY">Sequencial</MenuItem>
                                    <MenuItem value="NON_SEQUENTIALLY">Não sequencial</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                name="url"
                                label="URL de destino"
                                value={values.url}
                                onChange={handleChange}
                                helperText="Endpoint que receberá os eventos do Asaas"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="authToken"
                                label="Token de autenticação"
                                value={values.authToken}
                                onChange={handleChange}
                                helperText="Opcional se ASAAS_TOKEN_AUTH estiver no servidor. Mínimo de 32 caracteres."
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                options={PAYMENT_EVENTS}
                                value={values.events}
                                onChange={(e, newValue) => setFieldValue('events', newValue)}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip variant="outlined" size="small" label={option} {...getTagProps({ index })} key={option} />
                                    ))
                                }
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="Eventos monitorados"
                                        placeholder="Selecione os eventos"
                                        required
                                    />
                                )}
                            />
                            <FormHelperText>Eventos de pagamento sugeridos para o fluxo do PokeMed</FormHelperText>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="enabled"
                                        checked={values.enabled}
                                        onChange={e => setFieldValue('enabled', e.target.checked)}
                                    />
                                }
                                label="Webhook ativo"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="interrupted"
                                        checked={values.interrupted}
                                        onChange={e => setFieldValue('interrupted', e.target.checked)}
                                    />
                                }
                                label="Fila interrompida"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    {Boolean(values.id) && (
                        <LoadingButton
                            loading={submittingDelete}
                            color="error"
                            onClick={() => window.confirm('Confirma a remoção deste webhook?') && excluir(values.id)}
                        >
                            Excluir
                        </LoadingButton>
                    )}
                    <Button onClick={closeDialog}>Cancelar</Button>
                    <LoadingButton type="submit" loading={isSubmitting} variant="contained">
                        Salvar
                    </LoadingButton>
                </DialogActions>
                </form>
            </Dialog>
        </>
    );
}
